import { useCallback, useEffect, useState } from "react";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, getItem } from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";
import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { FieldErrors } from "../form/FieldErrors";
import { Spinner } from "react-bootstrap";
import Loading from "../indicators/Loading";
import TaxPayer from "../../models/tax-payer";
import TaxPayerFixedLimit from "../../models/tax-payer-fixed-limits";

interface TaxPayerFixedLimitsFormProps {
  taxPayer?: TaxPayer;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const TaxPayerFixedLimitsForm: React.FC<TaxPayerFixedLimitsFormProps> = ({
  taxPayer,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState<TaxPayerFixedLimit>({});

  const onMaxUsersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      const value = e.target.value;
      const parsed = parseInt(value);
      return {
        ...prev,
        maxUsers: value === "" || isNaN(parsed) ? null : parsed,
      };
    });
  };

  const onMaxDispatchPointsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimits((prev) => {
      const value = e.target.value;
      const parsed = parseInt(value);
      return {
        ...prev,
        maxDispatchPoints: value === "" || isNaN(parsed) ? null : parsed,
      };
    });
  };

  const onConnectivityAccessChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimits((prev) => {
      const value = e.target.value;
      let connectivityAccess: boolean | null = null;
      if (value === "true") connectivityAccess = true;
      else if (value === "false") connectivityAccess = false;
      return { ...prev, connectivityAccess };
    });
  };

  const connectivityAccessValue = () => {
    if (limits.connectivityAccess === true) return "true";
    if (limits.connectivityAccess === false) return "false";
    return "";
  };

  const onAccountingAccessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      const value = e.target.value;
      let accountingAccess: boolean | null = null;
      if (value === "true") accountingAccess = true;
      else if (value === "false") accountingAccess = false;
      return { ...prev, accountingAccess };
    });
  };

  const accountingAccessValue = () => {
    if (limits.accountingAccess === true) return "true";
    if (limits.accountingAccess === false) return "false";
    return "";
  };

  const onAiAccessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      const value = e.target.value;
      let aiAccess: boolean | null = null;
      if (value === "true") aiAccess = true;
      else if (value === "false") aiAccess = false;
      return { ...prev, aiAccess };
    });
  };

  const aiAccessValue = () => {
    if (limits.aiAccess === true) return "true";
    if (limits.aiAccess === false) return "false";
    return "";
  };

  const onSave = useCallback(async () => {
    if (taxPayer === undefined) {
      return;
    }
    setSubmitting(true);
    const newLimits = { ...limits, taxPayerId: taxPayer.id };

    const requestStatus = await createItem(
      `/tax_payers/${taxPayer.id}/fixed-limits/`,
      newLimits,
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }

      onSuccess();
    }

    if (shouldClearOnSuccess) {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, limits]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setLimits({});
  };

  const loadLimits = async (newTaxPayer?: TaxPayer) => {
    setLoading(true);
    if (newTaxPayer === undefined || newTaxPayer.id === undefined) {
      setLimits({});
      setLoading(false);
      return;
    }

    const requestStatus = await getItem<TaxPayerFixedLimit>(
      `/tax_payers/${newTaxPayer.id}/fixed-limits/`,
    );

    if (
      requestStatus.status !== SUCCESS &&
      requestStatus.status !== NOT_FOUND
    ) {
      let message = "Ha ocurrido un error";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }

      warningAlert(message);

      setLimits({});
      setLoading(false);
      return;
    }

    if (
      requestStatus.status === NOT_FOUND ||
      requestStatus.data === undefined
    ) {
      setLimits({});
      setLoading(false);
      return;
    }

    setLimits(requestStatus.data);
    setLoading(false);
  };

  useEffect(() => {
    loadLimits(taxPayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Max. Usuarios:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                onChange={onMaxUsersChange}
                placeholder="Sin límite"
                value={
                  limits.maxUsers === null || limits.maxUsers === undefined
                    ? ""
                    : limits.maxUsers
                }
              />
              <small className="text-muted">Vacío = sin límite</small>
              <FieldErrors
                errors={getFieldErrors("maxUsers", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Max. Puntos de Expedición:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                onChange={onMaxDispatchPointsChange}
                placeholder="Sin límite"
                value={
                  limits.maxDispatchPoints === null ||
                  limits.maxDispatchPoints === undefined
                    ? ""
                    : limits.maxDispatchPoints
                }
              />
              <small className="text-muted">Vacío = sin límite</small>
              <FieldErrors
                errors={getFieldErrors("maxDispatchPoints", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Acceso a Conectividad:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                value={connectivityAccessValue()}
                onChange={onConnectivityAccessChange}
              >
                <option value="">Sin definir (no se aplica)</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </CSelect>
              <FieldErrors
                errors={
                  getFieldErrors("connectivityAccess", errors) as string[]
                }
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Acceso a Contabilidad:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                value={accountingAccessValue()}
                onChange={onAccountingAccessChange}
              >
                <option value="">Sin definir (no se aplica)</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("accountingAccess", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Acceso a Inteligencia Artificial:</CLabel>
            </CCol>
            <CCol>
              <CSelect value={aiAccessValue()} onChange={onAiAccessChange}>
                <option value="">Sin definir (no se aplica)</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("aiAccess", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Salir
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default TaxPayerFixedLimitsForm;
